// ============================================
// PROFILE PAGE - i18n
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Camera, Loader2, Save, Award, Ship, Trash2, Eye, TrendingUp, Settings, DollarSign, Calendar, MapPin, FileText, Plane, ChevronDown, ChevronUp } from 'lucide-react';
import { useMyProfile, useUpdateProfile, useUploadAvatar, useDeleteAvatar, useRanks, useUpdatePreferences, useMyStats, useProfileViews, useMyCertificates, useMySeaService } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { getInitials, formatNumber, getCountries } from '@/utils';
import { CvPdfButton } from '@/components/CvPdfButton';

// Gemi türleri - getter trap bypass, doğrudan key-label
const VESSEL_TYPES: Record<string, { tr: string; en: string }> = {
  BULK_CARRIER: { tr: 'Dökme Yük Gemisi', en: 'Bulk Carrier' },
  TANKER: { tr: 'Tanker', en: 'Tanker' },
  CONTAINER: { tr: 'Konteyner Gemisi', en: 'Container Ship' },
  GENERAL_CARGO: { tr: 'Genel Kargo', en: 'General Cargo' },
  RORO: { tr: 'Ro-Ro', en: 'Ro-Ro' },
  PASSENGER: { tr: 'Yolcu Gemisi', en: 'Passenger Ship' },
  OFFSHORE: { tr: 'Açık Deniz', en: 'Offshore' },
  TUG: { tr: 'Römorkör', en: 'Tug' },
  YACHT: { tr: 'Yat', en: 'Yacht' },
  FISHING: { tr: 'Balıkçı Gemisi', en: 'Fishing Vessel' },
  OTHER: { tr: 'Diğer', en: 'Other' },
};
import { useRef, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchableSelect } from '@/components/SearchableSelect';
import toast from 'react-hot-toast';
import { config } from '@/config';
import { useTranslation } from '@crew-at-sea/i18n';
import { getApi } from '@/services/api';
import { commonApi } from '@crew-at-sea/api-client';
import { CvWizard } from '@/components';


export const Route = createFileRoute('/_authenticated/profile/')({
  component: ProfilePage,
});

type ProfileFormData = {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  nationality: string;
  gender?: string;
  phone?: string;
  city?: string;
  country?: string;
  bio?: string;
  rankId?: string;
  department?: string;
  experienceYears?: number;
  educationSchool?: string;
  educationDepartment?: string;
  educationDegree?: string;
  educationGraduationYear?: number;
};

function ProfilePage() {
  const { t, i18n } = useTranslation(['panel', 'common', 'enums']);
  const countries = useMemo(() => getCountries(i18n.language), [i18n.language]);
  const { user } = useAuthStore();
  const { data: profileResponse, isLoading } = useMyProfile();
  const { data: statsResponse } = useMyStats();
  const { data: viewsResponse } = useProfileViews(30);
  const { data: ranksResponse } = useRanks();
  const { data: certsResponse } = useMyCertificates();
  const { data: seaServiceResponse } = useMySeaService();
  // References - direkt API
  const { data: refsResponse } = useQuery({
    queryKey: ['references'],
    queryFn: () => getApi().get<any[]>('/references'),
  });
  const updateMutation = useUpdateProfile();
  const updatePreferencesMutation = useUpdatePreferences();
  const uploadAvatarMutation = useUploadAvatar();
  const deleteAvatarMutation = useDeleteAvatar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [isCompletionCollapsed, setIsCompletionCollapsed] = useState(true);

  const handlePdfClick = () => {
    pdfInputRef.current?.click();
  };

  const handlePdfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error(t('common:errors.validation.invalidFileType', { defaultValue: 'Lütfen sadece PDF dosyası seçin.' }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('documents.fileTooLarge', { defaultValue: 'Dosya boyutu 10MB\'dan küçük olmalıdır.' }));
        return;
      }
      setUploadingPdf(true);
      try {
        const uploadResult = await commonApi.uploadDocument(file);
        const fileUrl = uploadResult?.url || (uploadResult as any)?.data?.url;
        if (fileUrl) {
          await updateMutation.mutateAsync({ cvPdfUrl: fileUrl });
          toast.success(t('profile.toast.pdfUploaded'));
        } else {
          throw new Error('No URL returned');
        }
      } catch (err) {
        console.error('PDF upload error:', err);
        toast.error(t('profile.toast.pdfUploadError', { defaultValue: 'PDF yüklenirken hata oluştu.' }));
      } finally {
        setUploadingPdf(false);
        if (pdfInputRef.current) pdfInputRef.current.value = '';
      }
    }
  };

  const handleDeletePdf = async () => {
    if (window.confirm(t('profile.confirm.deletePdf', { defaultValue: 'PDF CV\'nizi silmek istediğinize emin misiniz?' }))) {
      try {
        await updateMutation.mutateAsync({ cvPdfUrl: null });
        toast.success(t('profile.toast.pdfDeleted'));
      } catch (err) {
        toast.error(t('profile.toast.pdfDeleteError', { defaultValue: 'PDF silinemedi.' }));
      }
    }
  };


  const profile: any = (profileResponse as any)?.data || profileResponse;
  const stats: any = (statsResponse as any)?.data || statsResponse || {};
  const viewsData: any = (viewsResponse as any)?.data || viewsResponse || {};
  const ranks: any[] = (ranksResponse as any)?.data || ranksResponse || [];
  const certificates: any[] = (certsResponse as any)?.data || certsResponse || [];
  const seaServiceRecords: any[] = (seaServiceResponse as any)?.data || seaServiceResponse || [];
  const references: any[] = (refsResponse as any)?.data || refsResponse || [];

  // Zod schema - t() ile çeviri desteği
  const profileSchema = useMemo(() => z.object({
    firstName: z.string().min(2, t('profile.validation.firstNameMin')),
    lastName: z.string().min(2, t('profile.validation.lastNameMin')),
    dateOfBirth: z.string().optional(),
    nationality: z.string().min(2, t('profile.validation.nationalityRequired')),
    gender: z.string().optional(),
    phone: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    bio: z.string().max(500, t('profile.validation.bioMax')).optional(),
    rankId: z.string().optional(),
    department: z.string().optional(),
    experienceYears: z.number().optional(),
    educationSchool: z.string().optional(),
    educationDepartment: z.string().optional(),
    educationDegree: z.string().optional(),
    educationGraduationYear: z.number().optional(),
  }), [t]);

  // Job Preferences State
  const [preferences, setPreferences] = useState({
    preferredVesselTypes: [] as string[],
    preferredContractLength: '',
    minSalary: '',
    maxSalary: '',
    availableFrom: '',
    willingToTravel: true,
    notes: '',
  });

  // Initialize preferences from profile
  useEffect(() => {
    if (profile) {
      setPreferences({
        preferredVesselTypes: profile.preferredVesselTypes || [],
        preferredContractLength: profile.preferredContractLength?.toString() || '',
        minSalary: profile.minSalary?.toString() || profile.minSalaryExpectation?.toString() || '',
        maxSalary: profile.maxSalary?.toString() || profile.maxSalaryExpectation?.toString() || '',
        availableFrom: profile.availableFrom ? profile.availableFrom.split('T')[0] : '',
        willingToTravel: profile.willingToTravel ?? true,
        notes: profile.notes || '',
      });
    }
  }, [profile]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    values: profile ? {
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
      nationality: profile.nationality || '',
      gender: profile.gender || '',
      phone: profile.phone || '',
      city: profile.city || '',
      country: profile.country || '',
      bio: profile.bio || '',
      rankId: profile.rankId || profile.rank?.id || '',
      department: profile.department || '',
      experienceYears: profile.experienceYears || 0,
      educationSchool: profile.educationSchool || '',
      educationDepartment: profile.educationDepartment || '',
      educationDegree: profile.educationDegree || '',
      educationGraduationYear: profile.educationGraduationYear || undefined,
    } : undefined,
  });

  const onSubmit = async (data: ProfileFormData) => {
    setIsSaving(true);
    try {
      // Save profile
      const formattedData = {
        ...data,
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth).toISOString() : undefined,
      };
      await updateMutation.mutateAsync(formattedData, { onSuccess: () => {} });

      // Save preferences
      await updatePreferencesMutation.mutateAsync({
        preferredVesselTypes: preferences.preferredVesselTypes,
        preferredContractLength: preferences.preferredContractLength ? parseInt(preferences.preferredContractLength) : undefined,
        minSalary: preferences.minSalary ? parseInt(preferences.minSalary) : undefined,
        maxSalary: preferences.maxSalary ? parseInt(preferences.maxSalary) : undefined,
        availableFrom: preferences.availableFrom ? new Date(preferences.availableFrom).toISOString() : undefined,
        willingToTravel: preferences.willingToTravel,
        notes: preferences.notes || undefined,
      }, { onSuccess: () => {} });

      toast.success(t('profile.toast.updateSuccess'));
    } catch (error) {
      toast.error(t('profile.toast.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleVesselType = (type: string) => {
    setPreferences((prev) => ({
      ...prev,
      preferredVesselTypes: prev.preferredVesselTypes.includes(type)
        ? prev.preferredVesselTypes.filter((t) => t !== type)
        : [...prev.preferredVesselTypes, type],
    }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Show preview immediately
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      
      uploadAvatarMutation.mutate(file, {
        onSuccess: () => {
          toast.success(t('profile.toast.avatarUpdated'));
          // Keep preview until profile data is refreshed
          // The useMyProfile hook will refetch and update profile.avatarUrl
          // Preview will be replaced by actual URL on next render
          setTimeout(() => {
            setPreviewUrl(null);
            URL.revokeObjectURL(objectUrl); // Clean up object URL
          }, 1000);
        },
        onError: () => {
          toast.error(t('profile.toast.avatarUploadError'));
          setPreviewUrl(null);
          URL.revokeObjectURL(objectUrl);
        },
      });
    }
  };

  const handleDeleteAvatar = () => {
    if (window.confirm(t('profile.confirm.deleteAvatar'))) {
      setPreviewUrl(null);
      deleteAvatarMutation.mutate(undefined, {
        onSuccess: () => toast.success(t('profile.toast.avatarDeleted')),
        onError: () => toast.error(t('profile.toast.avatarDeleteError')),
      });
    }
  };

  // Helper function to get full URL for uploads
  const getFullUrl = (path: string | null | undefined) => {
    if (!path) return null;
    // If it's already a full URL or data URL, return as is
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    // If it's a relative path starting with /uploads, prepend base URL
    if (path.startsWith('/uploads')) return `${config.uploadsBaseUrl}${path}`;
    return path;
  };

  const avatarUrl = previewUrl || getFullUrl(profile?.avatarUrl);
  const fullName = profile ? `${profile.firstName} ${profile.lastName}` : user?.email || t('common:user');
  const initials = getInitials(fullName);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('profile.title')}</h1>
          <p className="text-gray-500 mt-1">{t('profile.subtitle')}</p>
        </div>
        <div className="flex items-center justify-between gap-1.5 md:gap-2 w-full md:w-auto">
          {profile && (
            <>
              <button 
                type="button" 
                onClick={() => setShowWizard(true)} 
                className="btn-secondary text-maritime-600 border-maritime-200 hover:bg-maritime-50 flex-1 md:flex-none justify-center px-2 py-2 text-xs md:px-4 md:py-2 md:text-sm flex items-center gap-1.5 md:gap-2"
              >
                <Award className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="truncate">
                  <span className="hidden sm:inline">CV </span>
                  {i18n.language === 'tr' ? 'Güncelle' : 'Update'}
                </span>
              </button>
              <CvPdfButton
                profile={profile}
                certificates={certificates}
                seaServiceRecords={seaServiceRecords}
                references={references}
                className="flex-1 md:flex-none px-2 py-2 text-xs md:px-4 md:py-2 md:text-sm"
              />
            </>
          )}
          <Link to="/settings" className="btn-secondary flex-1 md:flex-none justify-center px-2 py-2 text-xs md:px-4 md:py-2 md:text-sm flex items-center gap-1.5 md:gap-2">
            <Settings className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span className="truncate">{t('profile.settings')}</span>
          </Link>
        </div>
      </div>

      {/* Profile Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Eye} title={t('profile.stats.profileViews')} value={stats?.profileViews || 0} change={stats?.profileViewsChange} color="blue" />
        <StatCard icon={TrendingUp} title={t('profile.stats.profileCompletion')} value={`%${profile?.profileCompleteness || 0}`} color="green" />
        <StatCard icon={Award} title={t('profile.stats.certificates')} value={stats?.totalCertificates || 0} subtitle={stats?.expiringCertificates > 0 ? t('profile.stats.expiringCertificates', { count: stats.expiringCertificates }) : undefined} color="purple" />
        <StatCard icon={Ship} title={t('profile.stats.seaService')} value={t('profile.stats.seaServiceDays', { count: stats?.totalSeaServiceDays || 0 })} color="teal" />
      </div>

      {/* Profile Completion Details */}
      {profile && (
        <div className="card">
          <div 
            onClick={() => setIsCompletionCollapsed(!isCompletionCollapsed)}
            className={`card-header flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors select-none ${isCompletionCollapsed ? 'border-b-0 rounded-maritime' : ''}`}
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('profile.completion.title')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('profile.completion.subtitle')}</p>
            </div>
            {isCompletionCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            )}
          </div>
          {!isCompletionCollapsed && (
            <div className="card-body">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{t('profile.completion.basicProfile')}</span>
                  <span className="text-sm font-semibold text-maritime-600">%{profile?.profileCompleteness || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-maritime-600 h-3 rounded-full transition-all duration-500" 
                    style={{ width: `${profile?.profileCompleteness || 0}%` }}
                  />
                </div>
              </div>
              
              {/* Temel Puanlar (100 puan) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ProfileCheckItem 
                  label={t('profile.check.fullName')}
                  points={10} 
                  completed={!!(profile?.firstName && profile?.lastName)} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.nationality')}
                  points={10} 
                  completed={!!profile?.nationality} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.dateOfBirth')}
                  points={10} 
                  completed={!!profile?.dateOfBirth} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.gender')}
                  points={10} 
                  completed={!!profile?.gender} 
                  link="#gender"
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.rank')}
                  points={10} 
                  completed={!!(profile?.currentRankId || profile?.rank)} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.department')}
                  points={10} 
                  completed={!!profile?.department} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.experienceYears')}
                  points={10} 
                  completed={profile?.experienceYears > 0} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.phone')}
                  points={10} 
                  completed={!!profile?.phone} 
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.cityCountry')}
                  points={10} 
                  completed={!!(profile?.city && profile?.country)} 
                  link="#location"
                  t={t}
                />
                <ProfileCheckItem 
                  label={t('profile.check.bio')}
                  points={10} 
                  completed={profile?.bio?.length >= 50} 
                  t={t}
                />
              </div>

              {/* Bonus Puanlar */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🎁</span>
                  <h3 className="text-md font-semibold text-gray-900">{t('profile.completion.bonusPoints')}</h3>
                  <span className="text-sm text-gray-500">({t('profile.completion.bonusHint')})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ProfileCheckItem 
                    label={t('profile.check.avatar')}
                    points={10} 
                    completed={!!profile?.avatarUrl} 
                    isBonus={true}
                    t={t}
                  />
                  <ProfileCheckItem 
                    label={t('profile.check.certificates')}
                    points={20} 
                    completed={(stats?.totalCertificates || 0) >= 1} 
                    link="/profile/certificates"
                    current={stats?.totalCertificates || 0}
                    target={1}
                    isBonus={true}
                    t={t}
                  />
                  <ProfileCheckItem 
                    label={t('profile.check.seaService')}
                    points={20} 
                    completed={(stats?.totalSeaServiceRecords || 0) >= 1} 
                    link="/profile/sea-service"
                    current={stats?.totalSeaServiceRecords || 0}
                    target={1}
                    isBonus={true}
                    t={t}
                  />
                  <ProfileCheckItem 
                    label={t('profile.pdfCv')}
                    points={10} 
                    completed={!!profile?.cvPdfUrl} 
                    isBonus={true}
                    t={t}
                  />
                </div>
              </div>
              
              {(profile?.profileCompleteness || 0) < 100 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>{t('profile.completion.tipLabel')}</strong> {t('profile.completion.tipText')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Profile Views Chart - DEVRE DIŞI */}
      {/* viewsData?.views && viewsData.views.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">{t('profile.chart.title')}</h2>
          </div>
          <div className="card-body">
            <div className="flex items-end gap-1 h-32">
              {viewsData.views.map((view: { date: string; views: number }) => {
                const maxViews = Math.max(...viewsData.views.map((v: { views: number }) => v.views), 1);
                const height = (view.views / maxViews) * 100;
                return (
                  <div key={view.date} className="flex-1 bg-maritime-100 hover:bg-maritime-200 rounded-t transition-colors relative group" style={{ height: `${Math.max(height, 4)}%` }}>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      {t('profile.chart.views', { count: view.views })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>{t('profile.chart.daysAgo')}</span>
              <span>{t('profile.chart.today')}</span>
            </div>
            <p className="text-center text-sm text-gray-600 mt-2">
              {t('profile.chart.total')}: <span className="font-semibold">{formatNumber(viewsData.total || 0)}</span> {t('profile.chart.viewsLabel')}
            </p>
          </div>
        </div>
      ) */}

      {/* Alternatif PDF CV Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">{t('profile.pdfCv')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('profile.pdfHint')}</p>
        </div>
        <div className="card-body">
          {profile?.cvPdfUrl ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center text-red-600 shrink-0">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 break-all">{profile.firstName}_{profile.lastName}_CV.pdf</p>
                  <p className="text-xs text-gray-500">{t('common:uploaded', { defaultValue: 'Yüklendi' })}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <a 
                  href={getFullUrl(profile.cvPdfUrl) || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-secondary py-2 px-3 text-xs justify-center w-full sm:w-auto"
                >
                  {t('profile.viewPdf')}
                </a>
                <button 
                  type="button" 
                  onClick={handleDeletePdf} 
                  className="btn-secondary py-2 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 justify-center w-full sm:w-auto"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('common:delete')}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button 
                type="button" 
                onClick={handlePdfClick} 
                className="btn-secondary w-full py-4 border-dashed border-2 border-gray-300 hover:border-maritime-500 flex flex-col items-center justify-center gap-2 transition-colors"
                disabled={uploadingPdf}
              >
                {uploadingPdf ? (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
                    <span className="text-sm font-medium text-gray-600">{t('profile.uploadingPdf')}</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-8 h-8 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{t('profile.uploadPdf')}</span>
                  </>
                )}
              </button>
              <input 
                ref={pdfInputRef} 
                type="file" 
                accept="application/pdf" 
                className="hidden" 
                onChange={handlePdfChange} 
              />
            </div>
          )}
        </div>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">{t('profile.form.title')}</h2>
        </div>

        <div className="card-body space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <button type="button" onClick={handleAvatarClick} className="relative w-24 h-24 rounded-full overflow-hidden bg-maritime-100 hover:bg-maritime-200 transition-colors group">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={fullName} className="w-full h-full object-cover" />
                ) : (
                  <span className="flex items-center justify-center w-full h-full text-2xl font-bold text-maritime-700">{initials}</span>
                )}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              {(uploadAvatarMutation.isPending || deleteAvatarMutation.isPending) && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-full">
                  <Loader2 className="w-6 h-6 animate-spin text-maritime-600" />
                </div>
              )}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{fullName}</h3>
              <p className="text-sm text-gray-500">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <button type="button" onClick={handleAvatarClick} className="text-sm text-maritime-600 hover:text-maritime-700">{t('profile.form.changePhoto')}</button>
                {avatarUrl && (
                  <>
                    <span className="text-gray-300">|</span>
                    <button type="button" onClick={handleDeleteAvatar} className="text-sm text-red-500 hover:text-red-600 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" />{t('common:delete')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="label">{t('profile.form.firstName')}</label>
              <input id="firstName" type="text" className={`input ${errors.firstName ? 'input-error' : ''}`} {...register('firstName')} />
              {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="label">{t('profile.form.lastName')}</label>
              <input id="lastName" type="text" className={`input ${errors.lastName ? 'input-error' : ''}`} {...register('lastName')} />
              {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
            </div>
            <div>
              <label htmlFor="dateOfBirth" className="label">{t('profile.form.dateOfBirth')}</label>
              <input id="dateOfBirth" type="date" className={`input ${errors.dateOfBirth ? 'input-error' : ''}`} {...register('dateOfBirth')} />
            </div>
            <div>
              <label htmlFor="nationality" className="label">{t('profile.form.nationality')}</label>
              <select id="nationality" className={`input ${errors.nationality ? 'input-error' : ''}`} {...register('nationality')}>
                <option value="">{t('profile.form.selectNationality')}</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div id="gender">
              <label htmlFor="gender" className="label">{t('profile.form.gender')}</label>
              <select id="gender" className="input" {...register('gender')}>
                <option value="">{t('common:select')}</option>
                <option value="MALE">{t('enums:gender.MALE')}</option>
                <option value="FEMALE">{t('enums:gender.FEMALE')}</option>
              </select>
            </div>
            <div>
              <label htmlFor="nationality" className="label">{t('profile.form.department')}</label>
              <select id="department" className="input" {...register('department')}>
                <option value="">{t('common:select')}</option>
                <option value="DECK">{t('enums:department.DECK')}</option>
                <option value="ENGINE">{t('enums:department.ENGINE')}</option>
                <option value="ELECTRICAL">{t('enums:department.ELECTRICAL')}</option>
                <option value="CATERING">{t('enums:department.CATERING')}</option>
              </select>
            </div>
            <div>
              <label htmlFor="rankId" className="label">{t('profile.form.rank')}</label>
              <Controller name="rankId" control={control} render={({ field }) => (
                <SearchableSelect options={ranks.map((rank: any) => ({ value: rank.id, label: i18n.language === 'en' ? (rank.nameEn || rank.nameTr) : (rank.nameTr || rank.nameEn) }))} value={field.value || ''} onChange={field.onChange} placeholder={t('profile.form.selectRank')} searchPlaceholder={t('profile.form.searchRank')} />
              )} />
            </div>
            <div>
              <label htmlFor="experienceYears" className="label">{t('profile.form.experienceYears')}</label>
              <input id="experienceYears" type="number" min="0" max="50" className="input" placeholder="0" {...register('experienceYears', { valueAsNumber: true })} />
            </div>
            <div>
              <label htmlFor="phone" className="label">{t('profile.form.phone')}</label>
              <input id="phone" type="tel" className="input" placeholder="+90 555 123 4567" {...register('phone')} />
            </div>
            <div>
              <label htmlFor="city" className="label">{t('profile.form.city')}</label>
              <input id="city" type="text" className="input" placeholder={t('profile.form.enterCity')} {...register('city')} />
            </div>
            <div id="location">
              <label htmlFor="country" className="label">{t('profile.form.country')}</label>
              <select id="country" className="input" {...register('country')}>
                <option value="">{t('profile.form.selectCountry')}</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="bio" className="label">{t('profile.form.bio')}</label>
            <textarea id="bio" rows={4} className={`input ${errors.bio ? 'input-error' : ''}`} placeholder={t('profile.form.bioPlaceholder')} {...register('bio')} />
            {errors.bio && <p className="mt-1 text-xs text-red-500">{errors.bio.message}</p>}
          </div>

          {/* Education - Eğitim Bilgileri */}
          <div>
            <label className="label font-semibold text-gray-800">{t('profile.form.educationTitle', { defaultValue: 'Eğitim Bilgileri' })}</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div className="md:col-span-2">
                <label htmlFor="educationSchool" className="label">{t('profile.form.educationSchool', { defaultValue: 'Okul Adı' })}</label>
                <input id="educationSchool" type="text" className="input" placeholder={t('profile.form.educationSchoolPlaceholder', { defaultValue: 'Üniversite veya okul adını giriniz' })} {...register('educationSchool')} />
              </div>
              <div>
                <label htmlFor="educationDepartment" className="label">{t('profile.form.educationDepartment', { defaultValue: 'Bölüm / Alan' })}</label>
                <input id="educationDepartment" type="text" className="input" placeholder={t('profile.form.educationDepartmentPlaceholder', { defaultValue: 'Örn. Denizcilik İşletmesi' })} {...register('educationDepartment')} />
              </div>
              <div>
                <label htmlFor="educationDegree" className="label">{t('profile.form.educationDegree', { defaultValue: 'Eğitim Derecesi' })}</label>
                <select id="educationDegree" className="input" {...register('educationDegree')}>
                  <option value="">{t('common:select')}</option>
                  <option value="PRIMARY">İlköğretim / Primary</option>
                  <option value="HIGH_SCHOOL">Lise / High School</option>
                  <option value="ASSOCIATE">Ön Lisans / Associate</option>
                  <option value="BACHELOR">Lisans / Bachelor</option>
                  <option value="MASTER">Yüksek Lisans / Master</option>
                  <option value="PHD">Doktora / PhD</option>
                </select>
              </div>
              <div>
                <label htmlFor="educationGraduationYear" className="label">{t('profile.form.educationGraduationYear', { defaultValue: 'Mezuniyet Yılı' })}</label>
                <input id="educationGraduationYear" type="number" min="1960" max={new Date().getFullYear()} className="input" placeholder={String(new Date().getFullYear())} {...register('educationGraduationYear', { valueAsNumber: true })} />
              </div>
            </div>
          </div>

          {/* Vessel Types */}
          <div>
            <label className="label flex items-center gap-2">
              <Ship className="w-4 h-4" />
              {t('profile.form.vesselTypes')}
            </label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(VESSEL_TYPES).map(([key, labels]) => (
                <button key={key} type="button" onClick={() => toggleVesselType(key)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${preferences.preferredVesselTypes.includes(key) ? 'bg-maritime-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {i18n.language === 'en' ? labels.en : labels.tr}
                </button>
              ))}
            </div>
          </div>

          {/* Contract Length */}
          <div>
            <label className="label flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t('profile.form.contractLength')}
            </label>
            <select 
              className="input" 
              value={preferences.preferredContractLength} 
              onChange={(e) => setPreferences({ ...preferences, preferredContractLength: e.target.value })}
            >
              <option value="">{t('common:select')}</option>
              <option value="3">{t('common:months', { count: 3 })}</option>
              <option value="4">{t('common:months', { count: 4 })}</option>
              <option value="5">{t('common:months', { count: 5 })}</option>
              <option value="6">{t('common:months', { count: 6 })}</option>
              <option value="8">{t('common:months', { count: 8 })}</option>
              <option value="9">{t('common:months', { count: 9 })}</option>
              <option value="12">{t('common:months', { count: 12 })}</option>
            </select>
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {t('profile.form.minSalary')}
              </label>
              <input type="number" className="input" placeholder="3000" value={preferences.minSalary} onChange={(e) => setPreferences({ ...preferences, minSalary: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('profile.form.maxSalary')}</label>
              <input type="number" className="input" placeholder="8000" value={preferences.maxSalary} onChange={(e) => setPreferences({ ...preferences, maxSalary: e.target.value })} />
            </div>
          </div>

          {/* Available From - Müsait Tarih */}
          <div>
            <label className="label flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t('profile.form.availableFrom', { defaultValue: 'Müsait Olduğunuz Tarih' })}
            </label>
            <input
              type="date"
              className="input"
              value={preferences.availableFrom}
              onChange={(e) => setPreferences({ ...preferences, availableFrom: e.target.value })}
            />
          </div>

          {/* Willing to Travel - Seyahata Açık */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plane className="w-4 h-4 text-gray-500" />
              <div>
                <p className="font-medium text-gray-900">{t('profile.form.willingToTravel', { defaultValue: 'Seyahate Açığım' })}</p>
                <p className="text-sm text-gray-500">{t('profile.form.willingToTravelHint', { defaultValue: 'Farklı ülke/limanlara seyahat edebilirsiniz' })}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={preferences.willingToTravel}
                onChange={(e) => setPreferences({ ...preferences, willingToTravel: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600"></div>
            </label>
          </div>

          {/* Notes - Notlar */}
          <div>
            <label className="label flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('profile.form.notes', { defaultValue: 'Notlar' })}
            </label>
            <textarea
              rows={3}
              className="input"
              placeholder={t('profile.form.notesPlaceholder', { defaultValue: 'Ek bilgiler, özel durumlar veya notlarınız...' })}
              value={preferences.notes}
              onChange={(e) => setPreferences({ ...preferences, notes: e.target.value })}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? (<><Loader2 className="w-4 h-4 animate-spin" />{t('common:saving')}</>) : (<><Save className="w-4 h-4" />{t('profile.form.saveChanges')}</>)}
            </button>
          </div>
        </div>
      </form>

      {/* Profile Wizard Modal */}
      {profile && (
        <CvWizard 
          open={showWizard} 
          onClose={() => setShowWizard(false)} 
          profile={profile} 
          onUpdate={(data) => updateMutation.mutateAsync(data)} 
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, title, value, change, subtitle, color }: { icon: any; title: string; value: number | string; change?: number; subtitle?: string; color: 'blue' | 'green' | 'purple' | 'teal'; }) {
  const colors = { blue: 'bg-blue-100 text-blue-600', green: 'bg-green-100 text-green-600', purple: 'bg-purple-100 text-purple-600', teal: 'bg-teal-100 text-teal-600' };
  return (
    <div className="card p-4">
      <div className={`w-10 h-10 rounded-maritime ${colors[color]} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
      <p className="text-sm text-gray-500">{title}</p>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change !== undefined && change !== 0 && (<span className={`text-xs flex items-center ${change > 0 ? 'text-green-600' : 'text-red-600'}`}><TrendingUp className={`w-3 h-3 ${change < 0 ? 'rotate-180' : ''}`} />{Math.abs(change)}%</span>)}
      </div>
      {subtitle && <p className="text-xs text-amber-600 mt-1">{subtitle}</p>}
    </div>
  );
}

function ProfileCheckItem({ 
  label, 
  points, 
  completed, 
  link, 
  current, 
  target,
  isBonus = false,
  t
}: { 
  label: string; 
  points: number; 
  completed: boolean; 
  link?: string;
  current?: number;
  target?: number;
  isBonus?: boolean;
  t: (key: string, options?: any) => string;
}) {
  const content = (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${
      completed 
        ? isBonus ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'
        : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
          completed 
            ? isBonus ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'
            : 'bg-gray-300 text-gray-600'
        }`}>
          {completed ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <span className="text-xs font-medium">{isBonus ? '★' : '!'}</span>
          )}
        </div>
        <div>
          <span className={`text-sm font-medium ${
            completed 
              ? isBonus ? 'text-amber-800' : 'text-green-800'
              : 'text-gray-700'
          }`}>{label}</span>
          {current !== undefined && target !== undefined && !completed && (
            <span className="text-xs text-gray-500 ml-2">({current}/{target})</span>
          )}
        </div>
      </div>
      <span className={`text-sm font-semibold ${
        completed 
          ? isBonus ? 'text-amber-600' : 'text-green-600'
          : 'text-gray-400'
      }`}>
        {completed ? `+${points}` : isBonus ? `+${points} ${t('profile.completion.bonus')}` : `${points} ${t('profile.completion.point')}`}
      </span>
    </div>
  );

  if (link && !completed) {
    return (
      <Link to={link as any} className="block hover:opacity-80 transition-opacity">
        {content}
      </Link>
    );
  }

  return content;
}
